import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Plus,
  Search,
  Filter,
  FolderOpen,
  Clock,
  DollarSign,
  Calendar,
  Eye,
  Edit,
  Play,
  Pause,
  CheckCircle,
  AlertCircle,
  Loader2
} from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Badge } from '@/components/ui/Badge'
import { formatCurrency, formatDate } from '@/utils'
import { projectsApi, apiClient } from '@/services/api'

interface Project {
  id: string
  name: string
  description: string
  status: string
  materialsCost: number
  timeLogged: number
  targetPrice: number
  createdAt: string
}

interface ProjectStats {
  activeProjects: number
  totalProjectValue: number
  hoursThisMonth: number
  completedThisMonth: number
  activeProjectsChange: number
  totalValueChange: number
  hoursChange: number
  completedChange: number
}

export const ProjectsListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedStatus, setSelectedStatus] = useState('all')
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid')
  const [projects, setProjects] = useState<Project[]>([])
  const [stats, setStats] = useState<ProjectStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const statusOptions = [
    { value: 'all', label: 'All Projects' },
    { value: 'planning', label: 'Planning' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'on-hold', label: 'On Hold' }
  ]

  // Fetch projects and stats from API
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)

        // Fetch projects and stats in parallel
        const [projectsResponse, statsResponse] = await Promise.all([
          projectsApi.getAll(),
          apiClient.get('/projects/stats')
        ])

        // projectsApi.getAll() now returns Project[] directly, not a paginated response
        setProjects(Array.isArray(projectsResponse) ? projectsResponse : [])
        setStats(statsResponse.data)
      } catch (err) {
        console.error('Error fetching projects data:', err)
        setError(err instanceof Error ? err.message : 'Failed to load projects')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  // Filter projects based on search and status
  const filteredProjects = projects.filter(project => {
    const matchesSearch = project.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         project.description.toLowerCase().includes(searchTerm.toLowerCase())
    const matchesStatus = selectedStatus === 'all' || project.status === selectedStatus
    return matchesSearch && matchesStatus
  })

  const statsCards = stats ? [
    {
      title: 'Active Projects',
      value: stats.activeProjects.toString(),
      change: stats.activeProjectsChange >= 0 ? `+${stats.activeProjectsChange}` : stats.activeProjectsChange.toString(),
      icon: FolderOpen,
      color: 'blue'
    },
    {
      title: 'Total Project Value',
      value: formatCurrency(stats.totalProjectValue),
      change: `${stats.totalValueChange >= 0 ? '+' : ''}${stats.totalValueChange.toFixed(1)}%`,
      icon: DollarSign,
      color: 'green'
    },
    {
      title: 'Hours This Month',
      value: stats.hoursThisMonth.toString(),
      change: `${stats.hoursChange >= 0 ? '+' : ''}${stats.hoursChange}h`,
      icon: Clock,
      color: 'purple'
    },
    {
      title: 'Completed This Month',
      value: stats.completedThisMonth.toString(),
      change: stats.completedChange >= 0 ? `+${stats.completedChange}` : stats.completedChange.toString(),
      icon: CheckCircle,
      color: 'emerald'
    }
  ] : []

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return 'secondary'
      case 'in-progress': return 'primary'
      case 'completed': return 'success'
      case 'on-hold': return 'warning'
      default: return 'secondary'
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'planning': return <Calendar className="w-4 h-4" />
      case 'in-progress': return <Play className="w-4 h-4" />
      case 'completed': return <CheckCircle className="w-4 h-4" />
      case 'on-hold': return <Pause className="w-4 h-4" />
      default: return <AlertCircle className="w-4 h-4" />
    }
  }

  return (
    <>
      <Helmet>
        <title>Projects - Jewelry Inventory Tracker</title>
      </Helmet>

      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Project Workspace
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              Manage jewelry projects with material tracking, time logging, and cost calculation
            </p>
          </div>
          <Link to="/projects/create">
            <Button leftIcon={<Plus className="w-4 h-4" />}>
              New Project
            </Button>
          </Link>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {loading ? (
            // Loading skeleton for stats
            Array.from({ length: 4 }).map((_, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="animate-pulse">
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                  <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                  <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
                </div>
              </div>
            ))
          ) : error ? (
            <div className="col-span-full bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <p className="text-red-600 dark:text-red-400">Error loading stats: {error}</p>
            </div>
          ) : (
            statsCards.map((stat) => (
              <div key={stat.title} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                      {stat.title}
                    </p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                      {stat.value}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/20`}>
                    <stat.icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                </div>
                <div className="mt-4">
                  <span className={`text-sm text-${stat.color}-600 dark:text-${stat.color}-400`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
                    from last month
                  </span>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Filters and Search */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
                />
              </div>
            </div>

            {/* Status Filter */}
            <div className="w-full lg:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => setSelectedStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-jewelry-primary focus:border-transparent"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            {/* View Mode Toggle */}
            <div className="flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden">
              <button
                onClick={() => setViewMode('grid')}
                className={`px-3 py-2 ${
                  viewMode === 'grid'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <FolderOpen className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-2 ${
                  viewMode === 'list'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                }`}
              >
                <Filter className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Projects Content */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          {loading ? (
            <div className="p-6">
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-jewelry-primary" />
                <span className="ml-2 text-gray-600 dark:text-gray-400">Loading projects...</span>
              </div>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                Error loading projects
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {error}
              </p>
              <Button onClick={() => window.location.reload()}>
                Try Again
              </Button>
            </div>
          ) : filteredProjects.length === 0 ? (
            <div className="text-center py-12">
              <FolderOpen className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                {projects.length === 0 ? 'No projects found' : 'No projects match your filters'}
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                {projects.length === 0
                  ? 'Create your first jewelry project to start tracking materials, time, and costs.'
                  : 'Try adjusting your search terms or filters to find projects.'
                }
              </p>
              {projects.length === 0 && (
                <Link to="/projects/create">
                  <Button leftIcon={<Plus className="w-4 h-4" />}>
                    Create Your First Project
                  </Button>
                </Link>
              )}
            </div>
          ) : (
            <div className="p-6">
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((project) => (
                    <div
                      key={project.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow"
                    >
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900 dark:text-white mb-2">
                            {project.name}
                          </h3>
                          <p className="text-sm text-gray-600 dark:text-gray-400 line-clamp-2">
                            {project.description}
                          </p>
                        </div>
                        <Badge variant={getStatusColor(project.status)} className="ml-2">
                          <div className="flex items-center gap-1">
                            {getStatusIcon(project.status)}
                            {project.status}
                          </div>
                        </Badge>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Materials Cost:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(project.materialsCost)}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Time Logged:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {project.timeLogged}h
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">Target Price:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {formatCurrency(project.targetPrice)}
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2">
                        <Link to={`/projects/${project.id}`} className="flex-1">
                          <Button variant="outline" size="sm" className="w-full">
                            <Eye className="w-3 h-3 mr-1" />
                            View
                          </Button>
                        </Link>
                        <Link to={`/projects/edit/${project.id}`}>
                          <Button variant="outline" size="sm">
                            <Edit className="w-3 h-3" />
                          </Button>
                        </Link>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 dark:border-gray-700">
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Project
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Status
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Materials Cost
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Time Logged
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Target Price
                        </th>
                        <th className="text-left py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Created
                        </th>
                        <th className="text-right py-3 px-4 font-medium text-gray-900 dark:text-white">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredProjects.map((project) => (
                        <tr
                          key={project.id}
                          className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                        >
                          <td className="py-3 px-4">
                            <div>
                              <div className="font-medium text-gray-900 dark:text-white">
                                {project.name}
                              </div>
                              <div className="text-sm text-gray-600 dark:text-gray-400 line-clamp-1">
                                {project.description}
                              </div>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant={getStatusColor(project.status)}>
                              <div className="flex items-center gap-1">
                                {getStatusIcon(project.status)}
                                {project.status}
                              </div>
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">
                            {formatCurrency(project.materialsCost)}
                          </td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">
                            {project.timeLogged}h
                          </td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">
                            {formatCurrency(project.targetPrice)}
                          </td>
                          <td className="py-3 px-4 text-gray-900 dark:text-white">
                            {formatDate(project.createdAt)}
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex justify-end gap-2">
                              <Link to={`/projects/${project.id}`}>
                                <Button variant="outline" size="sm">
                                  <Eye className="w-3 h-3" />
                                </Button>
                              </Link>
                              <Link to={`/projects/edit/${project.id}`}>
                                <Button variant="outline" size="sm">
                                  <Edit className="w-3 h-3" />
                                </Button>
                              </Link>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  )
}